<template>
    <div class="document-field-progress">
        <div class="progress-header">
            <h5>
                <i class="fa fa-tasks"></i>
                Completion Progress
            </h5>
            <span class="percentage-text">{{ completionPercentage }}%</span>
        </div>

        <div class="progress-bar-container">
            <div
                class="progress-bar"
                :class="progressClass"
                :style="{ width: completionPercentage + '%' }"
            >
                <span v-if="completionPercentage > 10" class="progress-label">
                    {{ completionPercentage }}%
                </span>
            </div>
        </div>

        <div class="progress-stats">
            <div class="stat-item completed">
                <i class="fa fa-check-circle"></i>
                <span>{{ completedCount }} Completed</span>
            </div>
            <div class="stat-item pending">
                <i class="fa fa-clock"></i>
                <span>{{ pendingCount }} Pending</span>
            </div>
        </div>

        <!-- Detailed Field Status -->
        <div v-if="showDetails" class="field-details">
            <div class="detail-section">
                <h6>Required Fields:</h6>
                <ul class="signature-checklist">
                    <!-- Document Level -->
                    <li
                        v-for="(signee, index) in documentLevelSignees"
                        :key="'doc-' + index"
                        :class="{ completed: signee.completed }"
                    >
                        <i :class="signee.completed ? 'fa fa-check-circle' : 'fa fa-circle'"></i>
                        <span
                            class="signee-badge-small"
                            :class="`badge-${signee.type}`"
                        >
                            {{ formatSigneeType(signee.type) }}
                        </span>
                        <span class="field-type-label">Document Field</span>
                    </li>

                    <!-- Field Level -->
                    <li
                        v-for="(signee, index) in fieldLevelSignees"
                        :key="'field-' + index"
                        :class="{ completed: signee.completed }"
                    >
                        <i :class="signee.completed ? 'fa fa-check-circle' : 'fa fa-circle'"></i>
                        <span
                            class="signee-badge-small"
                            :class="`badge-${signee.type}`"
                        >
                            {{ formatSigneeType(signee.type) }}
                        </span>
                        <span class="field-type-label">{{ signee.fieldName || 'Field' }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DocumentFieldProgress',
    props: {
        completionPercentage: {
            type: Number,
            required: true
        },
        requirements: {
            type: Object,
            default: () => ({
                document_level: [],
                field_level: {}
            })
        },
        completedSignatures: {
            type: Array,
            default: () => []
        },
        completedFields: {
            type: Array,
            default: () => []
        },
        showDetails: {
            type: Boolean,
            default: false
        },
        fields: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        progressClass() {
            if (this.completionPercentage === 100) {
                return 'complete';
            } else if (this.completionPercentage >= 50) {
                return 'in-progress-high';
            } else if (this.completionPercentage > 0) {
                return 'in-progress-low';
            }
            return 'not-started';
        },

        documentLevelSignees() {
            if (!this.requirements.document_level) return [];

            return this.requirements.document_level.map(signeeType => ({
                type: signeeType,
                level: 'document',
                completed: this.isFieldCompleted(signeeType, 'document')
            }));
        },

        fieldLevelSignees() {
            if (!this.requirements.field_level) return [];

            const signees = [];
            Object.entries(this.requirements.field_level).forEach(([fieldId, signeeTypes]) => {
                signeeTypes.forEach(signeeType => {
                    signees.push({
                        type: signeeType,
                        level: 'field',
                        fieldId: fieldId,
                        fieldName: this.getFieldName(fieldId),
                        completed: this.isFieldCompleted(signeeType, 'field', fieldId)
                    });
                });
            });

            return signees;
        },

        totalFields() {
            return this.documentLevelSignees.length + this.fieldLevelSignees.length;
        },

        completedCount() {
            // Use completedFields if provided, otherwise fall back to completedSignatures
            if (this.completedFields.length > 0) {
                return this.completedFields.length;
            }
            return this.completedSignatures.length;
        },

        pendingCount() {
            return this.totalFields - this.completedCount;
        }
    },
    methods: {
        formatSigneeType(type) {
            if (!type || type === 'null' || type === 'undefined' || type === 'unassigned') {
                return 'Unassigned';
            }
            const labels = {
                student: 'Student',
                guardian: 'Guardian',
                instructor: 'Instructor',
                staff: 'Staff',
                admin: 'Admin'
            };
            return labels[type] || type;
        },

        isFieldCompleted(signeeType, level, fieldId = null) {
            // Check completedFields first (includes all field types)
            if (this.completedFields.length > 0 && fieldId) {
                return this.completedFields.some(field =>
                    String(field.id) === String(fieldId)
                );
            }

            // Fall back to checking completedSignatures (for signature fields only)
            return this.completedSignatures.some(sig => {
                const typeMatch = sig.signee_type === signeeType;
                const levelMatch = level === 'document'
                    ? !sig.document_field_id
                    : sig.document_field_id;
                const fieldMatch = fieldId ? String(sig.document_field_id) === String(fieldId) : true;

                return typeMatch && levelMatch && fieldMatch;
            });
        },

        getFieldName(fieldId) {
            const field = this.fields.find(f => String(f.id) === String(fieldId));
            return field?.field_label || field?.label || field?.field_name || `Field ${fieldId}`;
        }
    }
};
</script>

<style scoped>
.document-field-progress {
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.progress-header h5 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;
}

.progress-header h5 i {
    color: #4a5568;
}

.percentage-text {
    font-size: 18px;
    font-weight: 700;
    color: #2d3748;
}

.progress-bar-container {
    width: 100%;
    height: 24px;
    background-color: #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
    position: relative;
}

.progress-bar {
    height: 100%;
    border-radius: 12px;
    transition: width 0.5s ease, background-color 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.progress-bar.not-started {
    background-color: #cbd5e0;
}

.progress-bar.in-progress-low {
    background: linear-gradient(90deg, #fcd34d 0%, #f59e0b 100%);
}

.progress-bar.in-progress-high {
    background: linear-gradient(90deg, #60a5fa 0%, #4299e1 100%);
}

.progress-bar.complete {
    background: linear-gradient(90deg, #34d399 0%, #10b981 100%);
}

.progress-label {
    font-size: 11px;
    font-weight: 700;
    color: #ffffff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.progress-stats {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
}

.stat-item.completed {
    color: #065f46;
}

.stat-item.completed i {
    color: #10b981;
}

.stat-item.pending {
    color: #92400e;
}

.stat-item.pending i {
    color: #f59e0b;
}

/* Detailed Signature Status */
.field-details {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.detail-section h6 {
    margin: 0 0 12px 0;
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
}

.signature-checklist {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.signature-checklist li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background-color: #f7fafc;
    border-radius: 4px;
    font-size: 13px;
}

.signature-checklist li.completed {
    background-color: #d1fae5;
}

.signature-checklist li i {
    font-size: 14px;
    color: #cbd5e0;
}

.signature-checklist li.completed i {
    color: #10b981;
}

.signee-badge-small {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-student {
    background-color: #3b82f6;
}

.badge-guardian {
    background-color: #10b981;
}

.badge-instructor {
    background-color: #f59e0b;
}

.badge-staff {
    background-color: #8b5cf6;
}

.badge-admin {
    background-color: #ef4444;
}

.badge-null,
.badge-undefined,
.badge-unassigned {
    background-color: #6b7280;
}

.field-type-label {
    color: #4a5568;
}

/* Mobile Optimization */
@media (max-width: 768px) {
    .document-field-progress {
        padding: 16px;
    }

    .progress-header h5 {
        font-size: 14px;
    }

    .percentage-text {
        font-size: 16px;
    }

    .progress-stats {
        flex-direction: column;
        gap: 8px;
    }

    .stat-item {
        font-size: 12px;
    }

    .signature-checklist li {
        font-size: 12px;
    }
}
</style>
